import * as Haptics from 'expo-haptics';
import i18n from '@/utils/i18n';
import React, { useEffect } from 'react';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { Button } from '@/components/button';
import { CloseButton } from '@/components/close-button';
import { HappyBox } from '@/components/happy-box';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { usePaymentsContext } from '@/contexts/payments-context';

export interface PaymentSuccessActionSheetProps {
  onClose: () => void;
  onPaymentSuccessAutoPayEnrollment: () => void;
}

export const PaymentSuccessComponent: React.FC<
  PaymentSuccessActionSheetProps
> = ({ onClose, onPaymentSuccessAutoPayEnrollment }) => {
  const { autoPayEnabled, autoPaySelfServeEnabled, chatAutoPaySetting } =
    usePaymentsContext();

  const showAutoPayEnrollmentButton =
    autoPayEnabled && autoPaySelfServeEnabled && !chatAutoPaySetting;

  return (
    <>
      <HappyBox confettiType="money" />
      <Text style={[TYPOGRAPHY.h3, styles.titleText]}>
        {i18n.t('payment_success.title')}
      </Text>
      <Text style={[TYPOGRAPHY.actionSheetMessageText, styles.messageText]}>
        {i18n.t(
          showAutoPayEnrollmentButton
            ? 'payment_success.confirmation_autopay_enrollment'
            : 'payment_success.confirmation',
        )}
      </Text>
      {showAutoPayEnrollmentButton ? (
        <Button
          containerStyle={styles.autopayEnrollmentContainer}
          onPress={() => {
            onPaymentSuccessAutoPayEnrollment();
            onClose();
          }}
          style={styles.autopayEnrollmentButton}
          textStyle={styles.autopayEnrollmentButtonText}
          trackingData={{
            name: 'payment.autopay_enrollment_button',
          }}
          type="secondary"
        >
          {i18n.t('enroll_in_autopay')}
        </Button>
      ) : null}
    </>
  );
};

export const PaymentSuccessActionSheet: React.FC<
  PaymentSuccessActionSheetProps
> = ({ onClose, onPaymentSuccessAutoPayEnrollment }) => {
  useEffect(() => {
    if (Platform.OS !== 'web') {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    }
  }, []);

  return (
    <BaseActionSheet contentStyle={styles.content} isVisible onClose={onClose}>
      <View style={styles.closeButtonContainer}>
        <CloseButton
          onPress={onClose}
          testID="payment-success-modal-close-button"
          trackingData={{
            name: 'payment_success_action_sheet.close_button',
          }}
        />
      </View>
      <PaymentSuccessComponent
        onClose={onClose}
        onPaymentSuccessAutoPayEnrollment={onPaymentSuccessAutoPayEnrollment}
      />
    </BaseActionSheet>
  );
};

const styles = StyleSheet.create({
  autopayEnrollmentButton: {
    borderRadius: 8,
    borderWidth: 1,
    height: 40,
    marginTop: 16,
  },
  autopayEnrollmentButtonText: {
    fontSize: 14,
  },
  autopayEnrollmentContainer: {
    alignSelf: 'center',
  },
  closeButtonContainer: {
    position: 'absolute',
    right: SCREEN_PADDING,
    top: SCREEN_PADDING,
  },
  content: {
    paddingHorizontal: 48,
    paddingTop: 24,
  },
  messageText: {
    textAlign: 'center',
  },
  titleText: {
    marginBottom: 8,
    textAlign: 'center',
  },
});
